import React, { useEffect, useState } from "react";
import {
  Carousel,
  CarouselControl,
  CarouselIndicators,
  CarouselItem,
  Col,
  Row,
} from "reactstrap";
import CurrentStockPopover from "./CurrentStockPopover";
import SalesByPricePopover from "./SalesByPricePopover";
import "./ProductOverview.css";

const formatCurrency = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const number = Number(value);
  return Number.isNaN(number)
    ? value
    : `£${number.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
};

const formatNumber = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const number = Number(value);
  return Number.isNaN(number) ? value : number.toLocaleString();
};

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
};

const ProductDetailItem = ({ label, value }) => (
  <Col sm={6}>
    <div className="d-flex align-items-center justify-content-between py-3 border-bottom h-100">
      <div className="text-muted small mr-3">{label}</div>
      <div className="font-weight-bold text-right" style={{ wordBreak: "break-word" }}>{value}</div>
    </div>
  </Col>
);

const RecentSalesItem = ({ days, value, color, backgroundColor }) => (
  <Col sm={4} className="mb-2">
    <div
      className="rounded p-3 h-100"
      style={{ backgroundColor, borderLeft: `4px solid ${color}` }}
    >
      <div className="text-muted small">Last {days} Days</div>
      <div className="font-weight-bold" style={{ color, fontSize: "20px", lineHeight: 1.2 }}>
        {formatNumber(value)}
      </div>
      <div className="text-muted" style={{ fontSize: "10px" }}>units sold</div>
    </div>
  </Col>
);

const ProductOverview = ({ productId, product, result, salesDetails, salesDetailsContent }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [animating, setAnimating] = useState(false);
  const images = Array.isArray(product?.images) ? product.images.filter(Boolean) : [];
  const carouselItems = images.map((src, index) => ({
    src,
    altText: `${product?.product_code || "Product"} ${product?.colour || ""} image ${index + 1}`.trim(),
  }));

  useEffect(() => {
    setActiveIndex(0);
  }, [product?.product_code, product?.colour]);

  if (!product) return null;

  const next = () => {
    if (animating || carouselItems.length < 2) return;
    setActiveIndex(activeIndex === carouselItems.length - 1 ? 0 : activeIndex + 1);
  };

  const previous = () => {
    if (animating || carouselItems.length < 2) return;
    setActiveIndex(activeIndex === 0 ? carouselItems.length - 1 : activeIndex - 1);
  };

  const goToIndex = (newIndex) => {
    if (!animating) setActiveIndex(newIndex);
  };

  return (
    <div className="border rounded p-3 mb-4" style={{ backgroundColor: "#f8f9fa" }}>
      <Row>
        <Col md={5} className="product-image-column mb-3 mb-md-0">
          {carouselItems.length === 0 ? (
            <div
              className="product-image-placeholder border rounded d-flex align-items-center justify-content-center text-muted"
              style={{ backgroundColor: "#fff" }}
            >
              No product images available
            </div>
          ) : (
            <Carousel
              className="product-image-carousel"
              activeIndex={activeIndex}
              next={next}
              previous={previous}
            >
              {carouselItems.length > 1 && (
                <CarouselIndicators items={carouselItems} activeIndex={activeIndex} onClickHandler={goToIndex} />
              )}
              {carouselItems.map((item) => (
                <CarouselItem
                  key={item.src}
                  onExiting={() => setAnimating(true)}
                  onExited={() => setAnimating(false)}
                >
                  <div
                    className="product-image-frame border rounded d-flex align-items-center justify-content-center"
                    style={{ overflow: "hidden", backgroundColor: "#fff" }}
                  >
                    <img src={item.src} alt={item.altText} style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                  </div>
                </CarouselItem>
              ))}
              {carouselItems.length > 1 && (
                <>
                  <CarouselControl direction="prev" directionText="Previous" onClickHandler={previous} />
                  <CarouselControl direction="next" directionText="Next" onClickHandler={next} />
                </>
              )}
            </Carousel>
          )}
        </Col>

        <Col md={7}>
          <div className="mb-3">
            <span
              className="badge badge-pill px-3 py-2"
              style={{ backgroundColor: "#e8f1ff", color: "#2457a7", fontSize: "12px" }}
            >
              Product and Stock
            </span>
          </div>
          <div className="rounded px-3" style={{ backgroundColor: "#fff" }}>
            <Row>
              <ProductDetailItem label="Product Code" value={result?.product_code || product.product_code || "-"} />
              <ProductDetailItem label="Grade" value={result?.grade || product.grade || "-"} />
              <ProductDetailItem label="Colour" value={product.colour || result?.colour || "-"} />
              <ProductDetailItem label="Published Date" value={formatDate(product.published_at)} />
              <ProductDetailItem label="Cost Price" value={formatCurrency(product.cost)} />
              <ProductDetailItem label="RRP" value={formatCurrency(product.original_price)} />
              <CurrentStockPopover
                index="overview"
                totalStock={result?.total_stock}
                variants={product.variants}
              />
              <SalesByPricePopover
                productId={productId}
                salePrice={result?.current_sale_price ?? product.current_sale_price}
              />
            </Row>
          </div>

          <h6 className="font-weight-bold mt-3 mb-2">Recent Sales</h6>
          <Row>
            <RecentSalesItem days={7} value={salesDetails?.sold_last_7d} color="#17a2b8" backgroundColor="#e8f7fa" />
            <RecentSalesItem days={30} value={salesDetails?.sold_last_30d} color="#6f42c1" backgroundColor="#f2ecfb" />
            <RecentSalesItem days={90} value={salesDetails?.sold_last_90d} color="#e66a00" backgroundColor="#fff1e6" />
          </Row>

          {salesDetailsContent}
        </Col>
      </Row>
    </div>
  );
};

export default ProductOverview;
